import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./index.css";

const BASE = process.env.REACT_APP_URL || "http://127.0.0.1:8000";
const API_KEY = process.env.REACT_APP_API_KEY;
const apiHeaders = () => ({
  "Content-Type": "application/json",
  ...(API_KEY ? { "x-api-key": API_KEY } : {}),
  "ngrok-skip-browser-warning": "true",
});

// ─── API ──────────────────────────────────────────────────────────────────────
async function fetchSymbolDetail(symbol) {
  const res = await fetch(`${BASE}/api/symbols/${symbol}/`, { headers: apiHeaders() });
  if (!res.ok) throw new Error(`Symbol not found: ${symbol}`);
  return res.json();
}

async function fetchChartData(symbol, fromDate, toDate) {
  const res = await fetch(
    `${BASE}/api/chart/${symbol}?from_date=${fromDate}&to_date=${toDate}&is_index=`,
    { headers: apiHeaders() }
  );
  if (!res.ok) throw new Error("Chart data unavailable");
  const data = await res.json();
  return (data.candles || []).map(c => ({
    date: c.timestamp,
    close: parseFloat(c.close_price),
    open: parseFloat(c.open_price),
    high: parseFloat(c.high_price),
    low: parseFloat(c.low_price),
    volume: c.volume,
    ldcp: parseFloat(c.ldcp),
  }));
}

async function fetchIntradayData(symbol) {
  const res = await fetch(`${BASE}/api/intraday/${symbol}`, { headers: apiHeaders() });
  if (!res.ok) throw new Error("Intraday data unavailable");
  const json = await res.json();
  return (json.data || [])
    .map(([ts, price, volume]) => ({
      date: new Date(ts * 1000).toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" }),
      close: price,
      volume,
      ldcp: null,
    }))
    .reverse();
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(d) { return d.toISOString().split("T")[0]; }
function getDateRange(days) {
  const to = new Date(), from = new Date();
  from.setDate(from.getDate() - days);
  return { from: formatDate(from), to: formatDate(to) };
}
function fmtNum(v, dec = 2) {
  if (v === null || v === undefined) return "—";
  return parseFloat(v).toFixed(dec);
}
function fmtLarge(v) {
  if (!v) return "—";
  return (v / 1e6).toFixed(2) + " mn";
}

// ─── Injected CSS for responsive layout ──────────────────────────────────────
const PAGE_CSS = `
.td-stat-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:10px;margin:1rem 1rem 0;}
.td-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:1rem;}
.td-grid-4{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;}
.td-range-btns{display:flex;gap:6px;}
.td-company-left{display:flex;align-items:center;gap:14px;}
.td-header-inner{display:flex;justify-content:space-between;align-items:center;}
@media(max-width:700px){
  .td-grid-2{grid-template-columns:1fr!important;}
  .td-grid-4{grid-template-columns:1fr 1fr!important;}
  .td-range-btns{flex-wrap:wrap!important;}
  .td-header-inner{flex-direction:column!important;align-items:flex-start!important;gap:12px!important;}
  .td-company-left{flex-direction:column!important;align-items:flex-start!important;}
}
@media(max-width:480px){
  .td-stat-row{grid-template-columns:1fr 1fr!important;}
}
`;
function injectStyles() {
  if (!document.getElementById("td-styles")) {
    const t = document.createElement("style");
    t.id = "td-styles";
    t.textContent = PAGE_CSS;
    document.head.appendChild(t);
  }
}

// ─── Range buttons ────────────────────────────────────────────────────────────
const RANGES = [
  { label: "1D", days: 0 },
  { label: "1W", days: 7 },
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "6M", days: 180 },
  { label: "1Y", days: 365 },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
const RangeBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: "4px 10px", borderRadius: 20, border: "1px solid",
    borderColor: active ? "#f98300" : "#e5e7eb",
    background: active ? "#fff7ed" : "#fff",
    color: active ? "#f98300" : "#666",
    fontWeight: active ? 600 : 400,
    fontSize: 12, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
  }}>{label}</button>
);

const InfoRow = ({ label, value }) => (
  <div className="data-row">
    <span className="data-label">{label}</span>
    <span className="data-value" style={{ textAlign: "right", maxWidth: "60%", wordBreak: "break-word" }}>
      {value ?? "—"}
    </span>
  </div>
);

const StatCard = ({ label, value, sub, color }) => (
  <div style={{
    background: "#fff", borderRadius: 10, border: "1px solid #f0f0f0",
    padding: "12px 14px", minWidth: 0,
  }}>
    <div style={{ fontSize: 11, color: "#aaa", marginBottom: 4, fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: "0.95rem", fontWeight: 700, color: color || "#050a30" }}>{value ?? "—"}</div>
    {sub && <div style={{ fontSize: 10, color: "#aaa", marginTop: 3 }}>{sub}</div>}
  </div>
);

const RangeBar = ({ label, low, high, current }) => {
  const noData = low == null || high == null;
  const pct = (!noData && current != null)
    ? Math.max(0, Math.min(100, ((current - low) / (high - low)) * 100))
    : null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
        <span style={{ fontSize: 12, color: "#888", fontWeight: 500 }}>{label}</span>
        {noData
          ? <span style={{ fontSize: 11, color: "#ccc" }}>No data</span>
          : <div style={{ display: "flex", gap: 8, fontSize: 12 }}>
              <span style={{ color: "#ef4444", fontWeight: 600 }}>L {fmtNum(low)}</span>
              <span style={{ color: "#ddd" }}>|</span>
              <span style={{ color: "#059669", fontWeight: 600 }}>H {fmtNum(high)}</span>
            </div>
        }
      </div>
      <div style={{ height: 6, background: "#f0f0f0", borderRadius: 6, position: "relative" }}>
        {!noData && (
          <div style={{
            position: "absolute", inset: 0, height: 6, borderRadius: 6,
            background: "linear-gradient(90deg,#ef4444 0%,#f98300 50%,#059669 100%)",
          }} />
        )}
        {pct !== null && (
          <div style={{
            width: 11, height: 11, background: "#050a30", borderRadius: "50%",
            position: "absolute", top: -2.5, border: "2px solid #fff",
            boxShadow: "0 1px 4px rgba(0,0,0,.25)",
            left: `calc(${pct.toFixed(1)}% - 5.5px)`,
          }} />
        )}
      </div>
    </div>
  );
};

const PerfTile = ({ label, value }) => {
  const isPos = value >= 0;
  return (
    <div style={{ background: "#f9fafb", borderRadius: 8, padding: "12px 10px", textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "#aaa", marginBottom: 6 }}>{label}</div>
      {value === null || value === undefined
        ? <div style={{ fontSize: 14, color: "#ccc" }}>—</div>
        : <>
            <div style={{ fontSize: 15, fontWeight: 700, color: isPos ? "#059669" : "#ef4444" }}>
              {isPos ? "+" : ""}{fmtNum(value)}%
            </div>
            <div style={{ marginTop: 6, height: 3, background: "#e5e7eb", borderRadius: 3 }}>
              <div style={{
                height: 3, borderRadius: 3,
                background: isPos ? "#059669" : "#ef4444",
                width: `${Math.min(100, Math.abs(value) * 3)}%`,
              }} />
            </div>
          </>
      }
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #f0f0f0",
      borderRadius: 8, padding: "8px 12px", fontSize: 12,
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    }}>
      <div style={{ fontWeight: 600, color: "#050a30", marginBottom: 4 }}>{label}</div>
      {payload.map(p => p.value != null && (
        <div key={p.name} style={{ color: p.color, marginBottom: 1 }}>
          {p.name}: <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const PSXDashboard = () => {
  const { symbol } = useParams();
  const history = useHistory();

  const [symbolData, setSymbolData] = useState(null);
  const [chartData, setChartData]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError]           = useState(null);
  const [activeRange, setActiveRange] = useState(RANGES[2]); // 1M

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    fetchSymbolDetail(symbol)
      .then(setSymbolData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [symbol]);

  useEffect(() => {
    if (!symbol) return;
    setChartLoading(true);
    const loader = activeRange.days === 0
      ? fetchIntradayData(symbol)
      : (() => { const { from, to } = getDateRange(activeRange.days); return fetchChartData(symbol, from, to); })();
    loader
      .then(setChartData)
      .catch(() => setChartData([]))
      .finally(() => setChartLoading(false));
  }, [symbol, activeRange]);

  if (loading) return (
    <div className="psx1-dashboard" style={{ padding: "5rem 2rem", textAlign: "center" }}>
      <div style={{ color: "#f98300" }}>Loading {symbol}…</div>
    </div>
  );

  if (error || !symbolData) return (
    <div className="psx1-dashboard" style={{ padding: "5rem 2rem", textAlign: "center" }}>
      <div style={{ color: "#ef4444", marginBottom: 16 }}>{error || "Symbol not found"}</div>
      <button onClick={() => history.push("/")} style={{
        background: "#f98300", color: "#fff", border: "none",
        padding: "10px 24px", borderRadius: 8, cursor: "pointer", fontSize: 14,
      }}>← Go Home</button>
    </div>
  );

  const d       = symbolData;
  const details = d.details || {};
  const stats   = d.stats || {};
  const ranges  = stats.ranges || {};
  const perf    = stats.performance || {};

  const lastPrice = stats.last_price != null ? parseFloat(stats.last_price) : null;
  const changeVal = stats.change_percent != null ? parseFloat(stats.change_percent) : null;
  const isPos     = changeVal != null ? changeVal >= 0 : true;

  const closes   = chartData.map(c => c.close).filter(Boolean);
  const minClose = closes.length ? Math.min(...closes) * 0.995 : "auto";
  const maxClose = closes.length ? Math.max(...closes) * 1.005 : "auto";

  return (
    <div className="psx1-dashboard">

      {/* ── HEADER ── */}
      <div className="psx1-dashboard-header">
        <div className="psx1-container">
          <div className="psx1-header-content td-header-inner">

            <div className="td-company-left">
              {/* Logo */}
              <div style={{
                width: 100, height:100, borderRadius: 8, background: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden", flexShrink: 0, border: "1px solid #f0f0f0",
              }}>
                <img
                  src={`https://www.tickeranalysts.com/images/logos/logo_${d.code.toLowerCase()}.svg`}
                  alt={d.code}
                  style={{ width: "90%", height: "90%", objectFit: "contain" }}
                  onError={e => {
                    e.target.style.display = "none";
                    e.target.parentNode.innerHTML =
                      `<span style="font-size:13px;font-weight:700;color:#f98300;font-family:monospace">${d.code}</span>`;
                  }}
                />
              </div>

              {/* Name + sector */}
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{
                    background: "#f98300", color: "#fff",
                    padding: "2px 9px", borderRadius: 5,
                    fontSize: 12, fontFamily: "monospace", letterSpacing: "0.05em", fontWeight: 700,
                  }}>{d.code}</span>
                  <h1 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, color: "#050a30" }}>
                    {d.name}
                  </h1>
                </div>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: "#888" }}>
                  {d.sector?.name || "—"}
                  {d.is_shariah && (
                    <span style={{
                      marginLeft: 8, background: "#d1fae5", color: "#065f46",
                      fontSize: 10, padding: "2px 7px", borderRadius: 4, fontWeight: 600,
                    }}>✓ Shariah</span>
                  )}
                  {!d.is_active && (
                    <span style={{
                      marginLeft: 8, background: "#fee2e2", color: "#991b1b",
                      fontSize: 10, padding: "2px 7px", borderRadius: 4, fontWeight: 600,
                    }}>Inactive</span>
                  )}
                </p>
              </div>
            </div>

            {/* Price */}
            <div className="price-badge">
              <div className="price-value" style={{ fontSize: "1.5rem" }}>
                {lastPrice != null ? `Rs. ${fmtNum(lastPrice)}` : "N/A"}
              </div>
              {changeVal != null && (
                <div style={{ marginTop: 4 }}>
                  <span className={`badge ${isPos ? "positive" : "negative"}`}>
                    {isPos ? "▲" : "▼"} {Math.abs(changeVal).toFixed(2)}%
                  </span>
                </div>
              )}
              {stats.volume && (
                <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
                  Vol: {parseInt(stats.volume).toLocaleString()}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      <div className="psx1-container">

        {/* ── STAT CARDS ── */}
        <div className="td-stat-row">
          <StatCard label="Last Price"   value={lastPrice ? `Rs. ${fmtNum(lastPrice)}` : "—"} color={isPos ? "#059669" : "#ef4444"} />
          <StatCard label="Change"       value={changeVal != null ? `${isPos?"+":""}${fmtNum(changeVal)}%` : "—"} color={isPos ? "#059669" : "#ef4444"} />
          <StatCard label="Volume"       value={stats.volume ? parseInt(stats.volume).toLocaleString() : "—"} />
          <StatCard label="Avg Volume(12M)"   value={stats.avg_volume ? parseInt(stats.avg_volume).toLocaleString() : "—"} />
          <StatCard label="Total Shares" value={fmtLarge(details.total_shares)} />
          <StatCard
            label="Free Float"
            value={fmtLarge(details.free_float)}
            sub={details.free_float && details.total_shares
              ? `${((details.free_float / details.total_shares) * 100).toFixed(1)}% of total`
              : null}
          />
          <StatCard label="Face Value" value={details.face_value ? `Rs. ${details.face_value}` : "—"} />
          <StatCard label="Year End"   value={details.year_end || "—"} />
        </div>

        {/* ── RANGES + PERFORMANCE ── */}
        <div className="td-grid-2" style={{ margin: "1rem 1rem 0" }}>

          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header"><h3 className="card-title">Price Ranges</h3></div>
            <div className="card-content">
              <RangeBar label="Day Range" low={ranges.day?.low}             high={ranges.day?.high}             current={lastPrice} />
              <RangeBar label="6 Months"  low={ranges.six_months?.low}   high={ranges.six_months?.high}   current={lastPrice} />
              <RangeBar label="52 Weeks"  low={ranges.fifty_two_week?.low}  high={ranges.fifty_two_week?.high}  current={lastPrice} />
            </div>
          </div>

          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header"><h3 className="card-title">Performance</h3></div>
            <div className="card-content">
              <div className="td-grid-4">
                <PerfTile label="1 Month"  value={perf["1_month"]} />
                <PerfTile label="3 Months" value={perf["3_months"]} />
                <PerfTile label="6 Months" value={perf["6_months"]} />
                <PerfTile label="1 Year"   value={perf["1_year"]} />
              </div>
            </div>
          </div>
        </div>

        {/* ── PRICE CHART ── */}
        <div className="card summary-card" style={{ marginTop: "1rem" }}>
          <div className="card-header">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <h3 className="card-title">Price Chart — {d.code}</h3>
              <div className="td-range-btns">
                {RANGES.map(r => (
                  <RangeBtn key={r.label} label={r.label} active={activeRange.label === r.label} onClick={() => setActiveRange(r)} />
                ))}
              </div>
            </div>
          </div>
          <div className="card-content">
            {chartLoading ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb" }}>Loading chart…</div>
            ) : chartData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb" }}>No chart data for this range</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#f98300" stopOpacity={0.18} />
                        <stop offset="95%" stopColor="#f98300" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#bbb" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                    <YAxis domain={[minClose, maxClose]} tick={{ fontSize: 10, fill: "#bbb" }} tickLine={false} axisLine={false} tickFormatter={v => v.toFixed(0)} width={44} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="close" name="Close"      stroke="#f98300" strokeWidth={2} fill="url(#priceGrad)" dot={false} activeDot={{ r: 4, fill: "#f98300" }} />
                    <Area type="monotone" dataKey="ldcp"  name="Prev Close" stroke="#cbd5e1" strokeWidth={1} strokeDasharray="4 4" fill="none" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 6 }}>
                  <ResponsiveContainer width="100%" height={60}>
                    <BarChart data={chartData} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                      <XAxis dataKey="date" hide />
                      <YAxis hide />
                      <Tooltip content={({ active, payload }) =>
                        active && payload?.length
                          ? <div style={{ background: "#fff", border: "1px solid #f0f0f0", borderRadius: 6, padding: "4px 8px", fontSize: 11 }}>
                              Vol: <strong>{payload[0]?.value?.toLocaleString()}</strong>
                            </div>
                          : null
                      } />
                      <Bar dataKey="volume" fill="#050a30" opacity={0.12} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ textAlign: "center", fontSize: 10, color: "#ccc" }}>Volume</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── COMPANY INFO + ABOUT ── */}
        <div className="td-grid-2" style={{ margin: "1rem 1rem 1.5rem" }}>
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header"><h3 className="card-title">Company Details</h3></div>
            <div className="card-content">
              <div className="data-rows">
                <InfoRow label="Sector"      value={d.sector?.name} />
                <InfoRow label="Sector Code" value={d.sector?.code} />
                <InfoRow label="Face Value"  value={details.face_value ? `Rs. ${details.face_value}` : null} />
                <InfoRow label="Year End"    value={details.year_end} />
                <InfoRow label="Auditor"     value={details.auditor} />
                <InfoRow label="Registrar"   value={details.registrar} />
                <InfoRow label="Address"     value={details.address} />
                <InfoRow label="Website"     value={
                  details.website
                    ? <a href={details.website} target="_blank" rel="noopener noreferrer"
                         style={{ color: "#f98300", textDecoration: "none", wordBreak: "break-all" }}>
                        {details.website}
                      </a>
                    : null
                } />
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header"><h3 className="card-title">About</h3></div>
            <div className="card-content">
              <p style={{ fontSize: 13, color: "#555", lineHeight: 1.75, margin: 0 }}>
                {details.description || "No description available."}
              </p>
              {(details.total_shares || details.free_float) && (
                <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div style={{ background: "#fff7ed", borderRadius: 8, padding: "12px", textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "#aaa", marginBottom: 4 }}>Total Shares</div>
                    <div style={{ fontWeight: 700, color: "#f98300" }}>{fmtLarge(details.total_shares)}</div>
                  </div>
                  <div style={{ background: "#f0fdf4", borderRadius: 8, padding: "12px", textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "#aaa", marginBottom: 4 }}>Free Float</div>
                    <div style={{ fontWeight: 700, color: "#059669" }}>{fmtLarge(details.free_float)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── OHLCV TABLE (historical only, not for 1D) ── */}
        {chartData.length > 0 && activeRange.days > 0 && (
          <div className="card summary-card" style={{ marginBottom: "2rem" }}>
            <div className="card-header"><h3 className="card-title">Recent OHLCV Data</h3></div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 520 }}>
                <thead>
                  <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                    {["Date", "Open", "High", "Low", "Close", "Volume", "Prev Close"].map(h => (
                      <th key={h} style={{
                        padding: "9px 14px", textAlign: h === "Date" ? "left" : "right",
                        fontWeight: 600, color: "#9ca3af", fontSize: 10,
                        textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...chartData].reverse().slice(0, 15).map((row, i) => {
                    const up = row.ldcp != null ? row.close >= row.ldcp : true;
                    return (
                      <tr key={row.date + i} style={{ borderBottom: "1px solid #f5f5f5", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={{ padding: "8px 14px", color: "#374151", fontWeight: 500, whiteSpace: "nowrap" }}>{row.date}</td>
                        {[row.open, row.high, row.low].map((v, j) => (
                          <td key={j} style={{ padding: "8px 14px", textAlign: "right", color: "#6b7280" }}>{v?.toFixed(2) ?? "—"}</td>
                        ))}
                        <td style={{ padding: "8px 14px", textAlign: "right", fontWeight: 700, color: up ? "#059669" : "#ef4444" }}>
                          {row.close?.toFixed(2) ?? "—"}
                        </td>
                        <td style={{ padding: "8px 14px", textAlign: "right", color: "#6b7280" }}>
                          {row.volume?.toLocaleString() ?? "—"}
                        </td>
                        <td style={{ padding: "8px 14px", textAlign: "right", color: "#9ca3af" }}>
                          {row.ldcp?.toFixed(2) ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PSXDashboard;